import { Component, Input, Output, EventEmitter, Inject, OnInit} from 'ng-metadata/core';
import './collection-header.component.scss';

type ViewMode = 'table' | 'cards';

/**
 * @ngdoc directive
 * @name mwCollectionHeader
 * @module mw.components.collection
 *
 * @param {string} [collectionType='table'] - The view type of collection, supports 'table' or 'cards'.
 * @param {boolean} [is-header-title-visible=false] - Control if the header title is visible.
 * @param {boolean} [is-search-active=false] - Control if search field is active.
 * @param {boolean} [is-search-visible=true] - Control if search is visible.
 * @param {boolean} [is-view-switch-visible=false] - Control if the view switch icon button is visible.
 * @param {string} [search-input-placeholder='Find'] - Placeholder text.
 * @param {string} [search-text] - Search text.
 * @param {expression} [on-change-collection-view] - Angular expression to evaluate when switch collection view button is clicked, $event is the view type string ('table' or 'cards').
 * @param {expression} [on-search] - Angular expression to evaluate when a search has been triggered, $event is the search text.
 * @param {expression} [on-search-toggle] - Angular expression to evaluate when search toggle button has been clicked, $event is boolean idicating active(true)/inactive(inactive) state.
 *
 *
 * @description
 * `<mw-collection-header>` is a component used to display filtering and searching features for data table or cards collection views.
 *
 * @usage
 * <hljs hljs-language="html">
 *   <mw-collection-header ng-if="!$ctrl.selectedItems.length" is-search-active="$ctrl.isSearchActive" on-search="$ctrl.search($event)" search-input-placeholder="Find Users" on-search-toggle="$ctrl.onSearchToggle($event)" on-change-collection-view="$ctrl.toggleView($event)">
 *     <mw-collection-header-title>
 *             <span class="mw-collection-header__title">Users</span>
 *     </mw-collection-header-title>
 *     <mw-collection-header-actions>
 *          <md-menu class="mw-menu" md-position-mode="target-right target" md-offset="0 50" >
 *              <mw-menu-trigger-icon-button icon="content:ic_sort_24px" tooltip="{{'Sort' | translate}}" on-click="$mdOpenMenu($event)"></mw-menu-trigger-icon-button>
 *              <md-menu-content class="mw-menu-dropdown" md-padding="md-padding" md-menu-align-target>
 *                  <mw-menu-item type="focusdummy" md-autofocus></mw-menu-item>
 *                  <mw-menu-item type="singleselect" aria-label="{{'Name' | translate}}" on-click="$ctrl.setOrderBy('title')" is-selected="$ctrl.orderBy === 'title'">{{'Name' | translate}}</mw-menu-item>
 *              </md-menu-content>
 *          </md-menu>
 *     </mw-collection-header-actions>
 *   </mw-collection-header>
 * </hljs>
 *
 */
@Component({
    selector: 'mw-collection-header',
    template: `
        <md-toolbar class="mw-collection-header__toolbar mw-collection-header__default-toolbar md-table-toolbar md-menu-toolbar md-default" ng-if="!$ctrl.selectedItems.length">
            <div class="md-toolbar-tools">
                <span layout="row" ng-transclude="mwCollectionHeaderTitle"></span>
                <div flex></div>
                <mw-collection-search ng-show="$ctrl.isSearchVisible" is-search-active="$ctrl.isSearchActive" on-search-toggle="$ctrl.handleSearchToggle($event)" search-input-placeholder="{{$ctrl.searchInputPlaceholder}}" search-text="{{$ctrl.searchText}}" on-search="$ctrl.handleSearch($event)" ng-class="{'mw-collection-search--search-active': !$ctrl.isHeaderTitleVisible}"></mw-collection-search>
                <div ng-if="$ctrl.isActionSlotFilled()" ng-transclude="mwCollectionHeaderActions" layout="row" layout-align="center" class="mw-collection-header__action-slot" ng-class="{'mw-collection-header__action-slot-compact': $ctrl.searchInputClassToggle && $ctrl.collectionType !== 'table' && $ctrl.collectionType !== 'cards', 'mw-collection-header__action-slot-xs': $ctrl.xsMedia}"></div>
                <md-button ng-if="$ctrl.isViewSwitchVisible && $ctrl.collectionType === 'table'" class="md-icon-button" aria-label="{{'mw-collection-header__switch-to-cards-view' | translate}}" ng-click="$ctrl.changeCollectionView('cards')">
                    <md-tooltip md-delay="500"><span translate="mw-collection-header__switch-to-cards-view"></span></md-tooltip>
                    <md-icon md-svg-icon="action:ic_view_module_24px"></md-icon>
                </md-button>
                <md-button ng-if="$ctrl.isViewSwitchVisible && $ctrl.collectionType === 'cards'" class="md-icon-button" aria-label="{{'mw-collection-header__switch-to-table-view' | translate}}" ng-click="$ctrl.changeCollectionView('table')">
                    <md-tooltip md-delay="500"><span translate="mw-collection-header__switch-to-table-view" ></span></md-tooltip>
                    <md-icon md-svg-icon="ic_view_list_24px"></md-icon>
                </md-button>
            </div>
        </md-toolbar>
    `,
    legacy: {
        transclude: {
            'mwCollectionHeaderTitle': 'mwCollectionHeaderTitle',
            'mwCollectionHeaderActions': '?mwCollectionHeaderActions',
        }
    }
})
export class CollectionHeaderComponent implements OnInit {
    @Input('@') collectionType: ViewMode = 'table';
    @Input('<') isHeaderTitleVisible: boolean = false;
    @Input('<') isSearchActive: boolean = false;
    @Input('<') isSearchVisible;
    @Input('<') isViewSwitchVisible: boolean = false;
    @Input('@') searchInputPlaceholder: string = 'Find';
    @Input('@') searchText: string;
    @Output() onChangeCollectionView = new EventEmitter<ViewMode>();
    @Output() onSearch = new EventEmitter<string>();
    @Output() onSearchToggle = new EventEmitter<boolean>();

    showSearch = true;

    constructor(@Inject('$transclude') private $transclude: ng.ITranscludeFunction,
                @Inject('$mdMedia') private $mdMedia,
                @Inject('$element') private $element: ng.IRootElementService) {
        'ngInject';
    }

    ngOnInit() {
        this.isSearchVisible = this.isSearchVisible === false ? false : true;
    }

    get xsMedia () {
        return this.$mdMedia('xs');
    }

    get searchInputClassToggle() {
        if (this.$element[0].getElementsByClassName('mw-collection-search__search-input-container').length > 0 && this.$mdMedia('xs')) {
            return true;
        } else {
            return false;
        }
    }

    isActionSlotFilled() {
        return this.$transclude.isSlotFilled('mwCollectionHeaderActions');
    }

    changeCollectionView(viewType: ViewMode) {
        this.onChangeCollectionView.emit(viewType);
    }

    handleSearch(searchText: string) {
        this.onSearch.emit(searchText);
    }

    handleSearchToggle(isSearchActive: boolean) {
        this.onSearchToggle.emit(isSearchActive);
    }
}




// WEBPACK FOOTER //
// ./src/lib/components/collection/collection-header/collection-header.component.ts